import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import cn from "classnames";
import Styles from "./SearchPage.module.scss";
import Sticky from "../../atoms/Sticky/StickyItem.module.scss";
import { Text } from "../../atoms/Text/Text";
import { SearchResults } from "../../compounds/SearchResults/SearchResults";
import { SearchInput } from "src/components/compounds/SearchInput/SearchInput";
import { FilterPill } from "src/components/compounds/FilterPill/FilterPill";
import { Spinner } from "src/components/atoms/Spinner";
import { ROUTES } from "src/utils/constants/routes";
import {
  useAddTeamByIDMutation,
  useFollowTeamPublicMutation,
  useSearchTeamByNameMutation,
  useUnfollowTeamMutation,
} from "src/state/modules/api";
import * as Eden from "src/eden/content";
import { TeamSearch } from "src/state/modules/team";
import { Title } from "src/components/atoms/Title/Title";
import { RecentlyViewed } from "src/components/compounds/RecentlyViewed/RecentlyViewed";
import { generateSeasonOptions, SPORT_OPTIONS, STATE_OPTIONS } from "src/utils/searchFilters";

const SEASON_OPTIONS = generateSeasonOptions();
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

export default function SearchPage() {
  const [addTeam] = useAddTeamByIDMutation();
  const [followTeamPublic] = useFollowTeamPublicMutation();
  const [unfollowTeam] = useUnfollowTeamMutation();
  const [searchTeam] = useSearchTeamByNameMutation();

  const [name, setName] = useState("");
  const [sport, setSport] = useState("baseball");
  const [seasons, setSeasons] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);

  const [results, setResults] = useState<Eden.SearchResults>();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  // Only auto-focus the search box on pointer (desktop) devices. On touch devices, auto-focusing
  // on load force-opens the keyboard and makes iOS Safari zoom/scroll to the field; the user can
  // just tap the box (it's 16px, so that tap won't zoom either).
  const autoFocusSearch = useMemo(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches,
    []
  );

  const runSearch = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults(undefined);
      return;
    }

    const query: TeamSearch = {
      sport,
      name: trimmed,
      seasons: seasons.map((value) => {
        const [seasonName, year] = value.split("_");
        return { name: seasonName, year };
      }),
      states,
    };

    setSearching(true);
    try {
      const { data } = await searchTeam(query);
      if (data !== undefined) {
        setResults(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  }, [name, sport, seasons, states, searchTeam]);

  // Search as the user types or changes a filter (debounced so we don't fire a
  // request on every keystroke). This matches the native mobile search feel and
  // removes the need for a separate "Submit" button.
  useEffect(() => {
    const handle = setTimeout(runSearch, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [runSearch]);

  const handleSubmit = (event: FormEvent) => {
    // Pressing the keyboard "search" / Enter key submits immediately.
    event.preventDefault();
    runSearch();
  };

  const importTeam = async (hit: Eden.SearchResult) => {
    setLoading(true);
    await followTeamPublic(hit.public_id);
    await addTeam(hit.id);
    await unfollowTeam(hit.id);
    navigate(generatePath(ROUTES.teams.home, { id: hit.id }));
  };

  if (loading) return <Spinner overlay label="Adding team…" />;

  const hasQuery = name.trim().length >= MIN_QUERY_LENGTH;
  const noResults = hasQuery && !searching && results && results.hits.length === 0;

  return (
    <div className={cn(Styles.searchPageWrapper)}>
      <Title text="Search"></Title>
      <div className={cn(Styles.searchHeader, Sticky.stickyItem)} data-sticky-name="search-header" data-sticky="true">
        <form onSubmit={handleSubmit} name="query">
          <SearchInput handleChange={(event) => setName(event.currentTarget.value)} autoFocus={autoFocusSearch} />
          <div className={Styles.filterWrapper}>
            <FilterPill
              label="Sport"
              options={SPORT_OPTIONS}
              selected={[sport]}
              onChange={(values) => setSport(values[0] ?? "baseball")}
            />
            <FilterPill label="Season" options={SEASON_OPTIONS} selected={seasons} onChange={setSeasons} multiple />
            <FilterPill label="State" options={STATE_OPTIONS} selected={states} onChange={setStates} multiple />
          </div>
        </form>
      </div>
      {!hasQuery && <RecentlyViewed />}
      <section id="results" aria-labelledby="search-results">
        {searching && (
          <Text className={Styles.statusMessage} size="small" color="cool-grey-dark" block>
            Searching…
          </Text>
        )}
        {results ? <SearchResults results={results} handleImport={importTeam} /> : null}
        {noResults && (
          <Text className={Styles.statusMessage} size="small" color="cool-grey-dark" block>
            No teams found. Try a different name or adjust your filters.
          </Text>
        )}
      </section>
    </div>
  );
}
